import React, { useState, useEffect, useMemo, useRef } from 'react';
import Chart from './ChartJsChart.js';
import { CHART_STATUS_COLORS } from '../utils/chartColors.js';
import Select from 'react-select';
import { Form, Col, Card, Row, Dropdown, Button, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMedal, faCog, faPlus, faEraser, faMagnifyingGlass, faUser, faCheckCircle, faTimesCircle, faCheckDouble, faUsers } from '@fortawesome/free-solid-svg-icons';
import Datetime from 'react-datetime';
import { formatInTimeZone } from 'date-fns-tz';
import generateExcel from 'zipcelx';
import Loader from './Loader.js';
import { ColumnSortingTable } from './Table.js';
import {
  batchQRCodeDownload,
  formatDateTimePickerValue,
  normalizeDateRange,
  normalizeDateTimePickerValue,
  PANEL_EVENT_TIME_ZONE,
  parseFlexibleDate,
} from '../assets/functions.js';
import ParticipantScoreHistoryModal from './ParticipantScoreHistoryModal.js';
import EventRsvpResponsesCard from './EventRsvpResponsesCard.js';
import ParticipantRecordsSummary from './ParticipantRecordsSummary.js';
import ReportDateRangeFilter from './ReportDateRangeFilter.js';
import EventRsvpManagerModal from './EventRsvpManagerModal.js';
import { getEventRsvpResponses } from '../api/rsvp.js';
import { participantRegistrationStatus, participantRecordRows, RECORD_REGISTRATION_LABELS } from '../utils/participantRecords.js';
import {
  buildPlatform2UserAction,
  PLATFORM_2_USER_ACTIONS,
} from './EventHistory02.js';

function useWindowWidth() {
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return width;
}

const CHART_PERIODS = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '3M', days: 90 },
];

const getChartEntryDate = (entry) => {
  const dateCandidates = [
    entry?.data_visitacao,
    entry?.creation_date,
    entry?.register_date,
    entry?.data_registro,
  ];

  return (
    dateCandidates
      .map((value) => parseFlexibleDate(value))
      .find((date) => !Number.isNaN(date.getTime())) || null
  );
};

const getChartDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return formatInTimeZone(date, PANEL_EVENT_TIME_ZONE, 'yyyy-MM-dd');
};

const credentialSelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const getDateFilterInputValue = (value) => {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && value._isValid !== false) {
    return normalizeDateTimePickerValue(value);
  }
  return "";
};

function generateChartDataForPeriod(entries, periodDays) {
  const monthsInPortuguese = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  const now = new Date();
  const categories = [];
  const dataCounts = [];
  const dateKeys = [];

  for (let i = periodDays - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    const dateKey = getChartDateKey(d);
    const day = formatInTimeZone(d, PANEL_EVENT_TIME_ZONE, 'dd');
    const monthIndex = Number(formatInTimeZone(d, PANEL_EVENT_TIME_ZONE, 'M')) - 1;
    const month = monthsInPortuguese[monthIndex];
    categories.push(`${day} ${month}`);
    dataCounts.push(0);
    dateKeys.push(dateKey);
  }

  if (entries) {
    entries.forEach(entry => {
      const entryDate = getChartEntryDate(entry);
      if (!entryDate) return;
      const idx = dateKeys.indexOf(getChartDateKey(entryDate));
      if (idx >= 0) dataCounts[idx]++;
    });
  }

  return {
    categories,
    series: [{ name: 'Total de registros', data: dataCounts }],
  };
}

export const ChartSection01 = ({ entries, dataTable }) => {
  const [chartPeriod, setChartPeriod] = useState(7);
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 576;
  const { categories, series } = generateChartDataForPeriod(entries, chartPeriod);

  const tickAmount = isMobile
    ? (chartPeriod === 7 ? 7 : chartPeriod === 30 ? 5 : 6)
    : (chartPeriod === 7 ? 7 : chartPeriod === 30 ? 6 : 7);

  const chartOptions = {
    chart: {
      zoom: { enabled: false },
      toolbar: {
        export: {
          csv: { filename: (dataTable?.titulo || 'chart') + " - Gráfico" },
          svg: { filename: (dataTable?.titulo || 'chart') + " - Gráfico" },
          png: { filename: (dataTable?.titulo || 'chart') + " - Gráfico" },
        },
      },
    },
    dataLabels: { enabled: chartPeriod === 7 && !isMobile },
    plotOptions: {
      bar: { borderRadius: 4, columnWidth: '60%' },
    },
    markers: { size: chartPeriod === 7 ? 0 : 4, style: 'hollow' },
    stroke: chartPeriod === 7
      ? { show: false }
      : { width: 3, curve: 'smooth' },
    xaxis: {
      categories,
      labels: {
        rotate: 0,
        hideOverlappingLabels: true,
      },
      tickAmount,
    },
    colors: ['#000'],
    tooltip: { y: [{ title: { formatter: (val) => val } }] },
    grid: { borderColor: '#f1f1f1' },
  };

  return (
    <>
      <Form.Group as={Col} className="textHeaderAplicativo mb-3">
        <h5 className="card-title mb-0">
          Total de registros ({dataTable?.total_participantes || 0})
        </h5>
      </Form.Group>
      <div className="d-flex gap-2 mx-3 mb-3">
        {CHART_PERIODS.map(opt => (
          <button
            key={opt.days}
            className={`btn btn-sm ${chartPeriod === opt.days ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setChartPeriod(opt.days)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="pb-3">
        <Card>
          <Card.Body>
            <div className="chart">
              <Chart
                key={`total-records-${chartPeriod}`}
                options={chartOptions}
                series={series}
                type={chartPeriod === 7 ? 'bar' : 'line'}
                height="350"
              />
            </div>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

export const ParticipantsSection01 = ({
  totalCounts, triggerExport, setTriggerExport, zipIsReady, setZipIsReady,
  filteredParticipants, dataTable, notyf, filter, setFilter, handleDelete, handleNewUser,
  handleDateRangeChange, getDefaultDateRangeMonth, fields, setFields, setFieldsResult,
  historyData, setFilteredParticipants, canEdit, canDownloadImages, isDownloadingImages,
  onDownloadImages, resetTrigger, setResetTrigger
}) => {
  return (
    <>
      <Form.Group
        as={Col}
        className="textHeaderAplicativo d-flex justify-content-between align-items-center"
      >
        <h5 className="card-title mb-0">
          Lista de participantes ({dataTable.total_participantes || 0})
        </h5>
        <div>
          <Dropdown className="d-inline">
            <Dropdown.Toggle className="btn btn-outline-primary">
              <FontAwesomeIcon icon={faCog} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {canDownloadImages && (
                <Dropdown.Item
                  disabled={isDownloadingImages}
                  onClick={onDownloadImages}
                >
                  {isDownloadingImages ? "Gerando ZIP..." : "Baixar fotos"}
                </Dropdown.Item>
              )}
              <Dropdown.Item
                onClick={() => {
                  setTriggerExport(true);
                }}
              >
                Exportar
              </Dropdown.Item>
              <Dropdown.Item
                disabled={!zipIsReady}
                onClick={() => {
                  if (zipIsReady) {
                    batchQRCodeDownload(
                      setZipIsReady,
                      filteredParticipants,
                      dataTable.titulo,
                      dataTable.token,
                      notyf,
                      dataTable.platform,
                    );
                  }
                }}
              >
                {zipIsReady ? "Exportar Códigos QR's" : "Gerando QR's..."}
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  setFilter((prevState) => ({
                    ...prevState,
                    extraPropsParticipants:
                      !prevState.extraPropsParticipants,
                  }));
                }}
              >
                {!filter.extraPropsParticipants
                  ? "Mostrar informações extras"
                  : "Esconder informações extras"}
              </Dropdown.Item>
              {canEdit && (
                <Dropdown.Item
                  onClick={() => {
                    let props = {
                      platform: dataTable.platform,
                      evento_id: dataTable.evento_id,
                      customizacao_id: filter.customizacao_id,
                      type: "removeUsers",
                    };
                    handleDelete(
                      props,
                      "Ao continuar, todos os participantes registrados serão excluídos.",
                    );
                  }}
                >
                  Excluir participantes
                </Dropdown.Item>
              )}
              {canEdit && (
                <Dropdown.Item
                  onClick={() => {
                    let props = {
                      platform: dataTable.platform,
                      evento_id: dataTable.evento_id,
                      customizacao_id: filter.customizacao_id,
                      type: "removeUsersHistory",
                    };
                    handleDelete(
                      props,
                      "Mantêm o registro mas remove os dados relacionados aos usuários",
                    );
                  }}
                >
                  Excluir apenas histórico do aplicativo
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
          {canEdit && (
            <Button
              type="button"
              className="btn btn-outline-primary ms-2"
              onClick={() => {
                handleNewUser(0);
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
            </Button>
          )}
        </div>
      </Form.Group>
      <h6 className="text-muted mt-3 mx-2">
        Encontre informações de registro e resultados associados aos
        participantes
      </h6>
      <div className="pb-3">
        <Row className="mb-3 mx-0 gx-2">
          <Form.Group as={Col} sm={12} md={4} className="mb-3">
            <Form.Select
              value={filter.customizacao_id}
              onChange={(e) => {
                setFields([]);
                setFieldsResult([]);
                setFilter((prevState) => ({
                  ...prevState,
                  customizacao_id: e.target.value,
                }));
              }}
            >
              <option value={""}>Nenhum</option>
              {dataTable.aplicativos.map((element, index) => {
                return (
                  <option
                    key={"fieldType" + index}
                    value={element.customizacao_id}
                  >
                    {element.titulo}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} xs={12} sm={6} md={3} className="mb-3">
            <InputGroup className="w-100 flex-nowrap">
              <div className="flex-fill">
                <Datetime
                  value={
                    filter.dateRange.startDate
                      ? formatDateTimePickerValue(filter.dateRange.startDate)
                      : ""
                  }
                  onChange={(date) => {
                    handleDateRangeChange("startDate", getDateFilterInputValue(date));
                  }}
                  dateFormat="DD/MM/YYYY"
                  timeFormat="HH:mm"
                  inputProps={{
                    placeholder: "dd/MM/yyyy HH:mm",
                    className: "form-control",
                    style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                  }}
                  locale="pt-br"
                />
              </div>
              <InputGroup.Text onClick={() => setResetTrigger && setResetTrigger(prev => prev + 1)} style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </InputGroup.Text>
            </InputGroup>
          </Form.Group>
          <Form.Group as={Col} xs={12} sm={6} md={3} className="mb-3">
            <InputGroup className="w-100 flex-nowrap">
              <div className="flex-fill">
                <Datetime
                  value={
                    filter.dateRange.endDate
                      ? formatDateTimePickerValue(filter.dateRange.endDate)
                      : ""
                  }
                  onChange={(date) => {
                    handleDateRangeChange("endDate", getDateFilterInputValue(date));
                  }}
                  dateFormat="DD/MM/YYYY"
                  timeFormat="HH:mm"
                  inputProps={{
                    placeholder: "dd/MM/yyyy HH:mm",
                    className: "form-control",
                    style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                  }}
                  locale="pt-br"
                />
              </div>
              <InputGroup.Text onClick={() => setResetTrigger && setResetTrigger(prev => prev + 1)} style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </InputGroup.Text>
            </InputGroup>
          </Form.Group>
          <Col md={2} className="d-flex align-items-end mb-3">
            <Button
              variant="primary"
              onClick={() => {
                setFilter((prev) => ({
                  ...prev,
                  dateRange: getDefaultDateRangeMonth(),
                }));
                if (setResetTrigger) setResetTrigger(prev => prev + 1);
              }}
              title="Limpar filtros"
            >
              <FontAwesomeIcon icon={faEraser} />
            </Button>
          </Col>
        </Row>
        {fields.length === 0 ? (
          <div className="py-5">
            <Loader></Loader>
          </div>
        ) : (
          <ColumnSortingTable
            columns={fields}
            title={dataTable.titulo}
            data={historyData.history}
            getCellProps={(cellinfo) => ({
              className:
                cellinfo.column.id === "icons" ? "table-icons-triple" : "",
              style: {
                textAlign:
                  cellinfo.column.id === "icons" ||
                    cellinfo.column.id === "media" ||
                    cellinfo.column.id === "time" ||
                    cellinfo.column.id === "score" ||
                    cellinfo.column.id === "currentQuestion"
                    ? "center"
                    : "",
                width: cellinfo.column.id === "media" ? "60px" : "",
                className:
                  cellinfo.column.id === "icons"
                    ? "table-icons-triple"
                    : "",
              },
            })}
            triggerExport={triggerExport}
            setTriggerExport={setTriggerExport}
            onFilteredDataChange={setFilteredParticipants}
          />
        )}
      </div>
    </>
  );
};

export const ResultsSection01 = ({
  totalCounts, triggerExportResult, setTriggerExportResult, filter, setFilter,
  dataTable, handleDelete, handleDateRangeChange, getDefaultDateRangeMonth,
  fieldsResult, historyData, setFilteredResults, canEdit, resetTrigger, setResetTrigger
}) => {
  const selectedApplication = dataTable.aplicativos?.find(
    (app) => parseInt(app.customizacao_id, 10) === parseInt(filter.customizacao_id, 10),
  );
  const isApp11 = parseInt(selectedApplication?.aplicativo_id, 10) === 11;
  const shouldShowResults = fieldsResult.length > 0 && filter.customizacao_id > 0 && !isApp11;

  return (
    <>
      {shouldShowResults && (
        <>
          <Form.Group
            as={Col}
            className="textHeaderAplicativo d-flex justify-content-between align-items-center"
          >
            <h5 className="card-title mb-0">
              Resultados ({totalCounts.results})
            </h5>
            <Dropdown className="d-inline me-2">
              <Dropdown.Toggle className="btn-outline-primary">
                <FontAwesomeIcon icon={faCog} />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => {
                    setTriggerExportResult(true);
                  }}
                >
                  Exportar
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    setFilter((prevState) => ({
                      ...prevState,
                      extraPropsResults: !prevState.extraPropsResults,
                    }));
                  }}
                >
                  {!filter.extraPropsResults
                    ? "Mostrar informações adicionais"
                    : "Esconder informações adicionais"}
                </Dropdown.Item>
                {canEdit && (
                  <Dropdown.Item
                    onClick={() => {
                      let props = {
                        platform: dataTable.platform,
                        evento_id: dataTable.evento_id,
                        customizacao_id: filter.customizacao_id,
                        type: "removeUsersResults",
                      };
                      handleDelete(
                        props,
                        "Ao continuar todos os resultados relacionados ao aplicativo serão removidos",
                      );
                    }}
                  >
                    Excluir todos os resultados
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>
          <h6 className="text-muted m-3">
            Histórico de resultados do aplicativo
          </h6>
          <Row className="mb-3 mx-0 gx-2">
            <Form.Group as={Col} xs={12} sm={6} md={4} className="mb-3">
              <InputGroup className="w-100 flex-nowrap">
                <div className="flex-fill">
                  <Datetime
                    value={
                      filter.dateRange.startDate
                        ? formatDateTimePickerValue(filter.dateRange.startDate)
                        : ""
                    }
                    onChange={(date) => {
                      handleDateRangeChange("startDate", getDateFilterInputValue(date));
                    }}
                    dateFormat="DD/MM/YYYY"
                    timeFormat="HH:mm"
                    inputProps={{
                      placeholder: "dd/MM/yyyy HH:mm",
                      className: "form-control",
                      style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                    }}
                    locale="pt-br"
                  />
                </div>
                <InputGroup.Text onClick={() => setResetTrigger && setResetTrigger(prev => prev + 1)} style={{ cursor: 'pointer' }}>
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </InputGroup.Text>
              </InputGroup>
            </Form.Group>
            <Form.Group as={Col} xs={12} sm={6} md={4} className="mb-3">
              <InputGroup className="w-100 flex-nowrap">
                <div className="flex-fill">
                  <Datetime
                    value={
                      filter.dateRange.endDate
                        ? formatDateTimePickerValue(filter.dateRange.endDate)
                        : ""
                    }
                    onChange={(date) => {
                      handleDateRangeChange("endDate", getDateFilterInputValue(date));
                    }}
                    dateFormat="DD/MM/YYYY"
                    timeFormat="HH:mm"
                    inputProps={{
                      placeholder: "dd/MM/yyyy HH:mm",
                      className: "form-control",
                      style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                    }}
                    locale="pt-br"
                  />
                </div>
                <InputGroup.Text onClick={() => setResetTrigger && setResetTrigger(prev => prev + 1)} style={{ cursor: 'pointer' }}>
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </InputGroup.Text>
              </InputGroup>
            </Form.Group>
            <Col md={2} className="d-flex align-items-end mb-3">
              <Button
                variant="primary"
                onClick={() => {
                  setFilter((prev) => ({
                    ...prev,
                    dateRange: getDefaultDateRangeMonth(),
                  }));
                  if (setResetTrigger) setResetTrigger(prev => prev + 1);
                }}
                title="Limpar filtros"
              >
                <FontAwesomeIcon icon={faEraser} />
              </Button>
            </Col>
          </Row>
          <ColumnSortingTable
            columns={fieldsResult}
            title={dataTable.titulo}
            data={historyData.results}
            getCellProps={(cellinfo) => ({
              style: {
                textAlign:
                  cellinfo.column.id === "icons" ||
                    cellinfo.column.id === "media" ||
                    cellinfo.column.id === "time" ||
                    cellinfo.column.id === "score" ||
                    cellinfo.column.id === "currentQuestion"
                    ? "center"
                    : "",
                width:
                  cellinfo.column.id === "icons" ||
                    cellinfo.column.id === "media"
                    ? "60px"
                    : "",
              },
            })}
            triggerExport={triggerExportResult}
            setTriggerExport={setTriggerExportResult}
            onFilteredDataChange={setFilteredResults}
            resetTrigger={resetTrigger}
          />
        </>
      )}
    </>
  );
};

export const ChartSection02 = ({ entries, dataTable, showHeader = true }) => {
  const [chartPeriod, setChartPeriod] = useState(7);
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 576;
  const { categories, series } = generateChartDataForPeriod(entries, chartPeriod);

  const tickAmount = isMobile
    ? (chartPeriod === 7 ? 7 : chartPeriod === 30 ? 5 : 6)
    : (chartPeriod === 7 ? 7 : chartPeriod === 30 ? 6 : 7);

  const chartOptions = {
    chart: {
      zoom: { enabled: false },
      toolbar: {
        export: {
          csv: { filename: (dataTable?.titulo || 'chart') + " - Gráfico" },
          svg: { filename: (dataTable?.titulo || 'chart') + " - Gráfico" },
          png: { filename: (dataTable?.titulo || 'chart') + " - Gráfico" },
        },
      },
    },
    dataLabels: { enabled: chartPeriod === 7 && !isMobile },
    plotOptions: {
      bar: { borderRadius: 4, columnWidth: '60%' },
    },
    markers: { size: chartPeriod === 7 ? 0 : 4, style: 'hollow' },
    stroke: chartPeriod === 7
      ? { show: false }
      : { width: 3, curve: 'smooth' },
    xaxis: {
      categories,
      labels: {
        rotate: 0,
        hideOverlappingLabels: true,
      },
      tickAmount,
    },
    colors: [CHART_STATUS_COLORS.success],
    tooltip: { y: [{ title: { formatter: (val) => val } }] },
    grid: { borderColor: '#f1f1f1' },
  };

  return (
    <>
      {showHeader && <Form.Group as={Col} className="textHeaderAplicativo mb-3">
        <h5 className="card-title mb-0">
          Total de registros ({dataTable?.total_participantes || 0})
        </h5>
      </Form.Group>}
      <div className="d-flex gap-2 mx-3 mb-3">
        {CHART_PERIODS.map(opt => (
          <button
            key={opt.days}
            className={`btn btn-sm ${chartPeriod === opt.days ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setChartPeriod(opt.days)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="mb-3 p-2">
        <Card>
          <Card.Body>
            <div className="chart">
              <Chart
                key={`total-records-${chartPeriod}`}
                options={chartOptions}
                series={series}
                type={chartPeriod === 7 ? 'bar' : 'line'}
                height="350"
              />
            </div>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

export const UsersSection02 = ({
  historyData, triggerExportUsers, setTriggerExportUsers, zipIsReady, setZipIsReady,
  filteredUsers, dataTable, notyf, filter, setFilter, handleDelete, handleNewUser,
  fieldsUsers, setFilteredUsers, canEdit, resetTrigger,
  handleDateRangeChange, getDefaultDateRangeMonth
}) => {
  const [userTableReset, setUserTableReset] = useState(0);
  return (
    <section className="mb-4" aria-label="Lista de usuários">
      {/* Lista de usuários */}
      <Form.Group
        as={Col}
        className="textHeaderAplicativo d-flex justify-content-between align-items-center"
      >
        <h5 className="card-title mb-0">
          Lista de usuários (
          {historyData.totalUsers || historyData.users.length})
        </h5>
        <div>
          <Dropdown className="d-inline">
            <Dropdown.Toggle className="btn-outline-primary">
              <FontAwesomeIcon icon={faCog} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => {
                  setTriggerExportUsers(true);
                }}
              >
                Exportar
              </Dropdown.Item>
              <Dropdown.Item
                disabled={!zipIsReady}
                onClick={() => {
                  if (zipIsReady) {
                    const qrReadyUsers = (filteredUsers || []).filter(
                      (user) => Number(user.exhibitor_id) > 0,
                    );

                    if (qrReadyUsers.length === 0) {
                      notyf.open({
                        type: "danger",
                        message:
                          "Nenhum usuário expositor com associação disponível para exportar códigos QR.",
                        ripple: true,
                        dismissible: true,
                      });
                      return;
                    }

                    if (qrReadyUsers.length !== (filteredUsers || []).length) {
                      notyf.open({
                        type: "default",
                        message: `${(filteredUsers || []).length - qrReadyUsers.length} usuário(s) gestor(es) ou sem associação foram ignorados na exportação.`,
                        ripple: true,
                        dismissible: true,
                      });
                    }

                    batchQRCodeDownload(
                      setZipIsReady,
                      qrReadyUsers,
                      dataTable.title,
                      dataTable.token,
                      notyf,
                      dataTable.platform,
                    );
                  }
                }}
              >
                {zipIsReady ? "Exportar Códigos QR's" : "Gerando QR's..."}
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  setFilter((prevState) => ({
                    ...prevState,
                    extraPropsUsers: !prevState.extraPropsUsers,
                  }));
                }}
              >
                {!filter.extraPropsUsers
                  ? "Mostrar informações adicionais"
                  : "Esconder informações adicionais"}
              </Dropdown.Item>
              {canEdit && (
                <Dropdown.Item
                  onClick={() => {
                    const props = buildPlatform2UserAction({
                      dataTable,
                      action: PLATFORM_2_USER_ACTIONS.DELETE,
                      userType: 1,
                    });
                    handleDelete(
                      props,
                      "Ao continuar, todos os usuários registrados serão excluídos.",
                    );
                  }}
                >
                  Excluir usuários
                </Dropdown.Item>
              )}
              {canEdit && (
                <Dropdown.Item
                  onClick={() => {
                    const props = buildPlatform2UserAction({
                      dataTable,
                      action: PLATFORM_2_USER_ACTIONS.RESET_HISTORY,
                      userType: 1,
                    });
                    handleDelete(
                      props,
                      "Mantém o gestor e o expositor, mas remove todo o histórico de uso, incluindo analytics, transmissões, chat, Newsfeed, pontuação e atividades.",
                    );
                  }}
                >
                  Excluir histórico do gestor/expositor
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
          {canEdit && (
            <Button
              type="button"
              className="btn btn-outline-primary ms-2"
              onClick={() => {
                handleNewUser(1);
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
            </Button>
          )}
        </div>
      </Form.Group>
      <h6 className="text-muted mt-3 mx-2">
        Consulte os dados coletados pelos usuários gestores e expositores
      </h6>

      <Row className="mb-3 mx-0 gx-2">
        <Form.Group as={Col} xs={12} sm={6} md={4} className="mb-3">
          <InputGroup className="w-100 flex-nowrap">
            <div className="flex-fill">
              <Datetime
                value={
                  filter.dateRange.startDate
                    ? formatDateTimePickerValue(filter.dateRange.startDate)
                    : ""
                }
                onChange={(date) => {
                  handleDateRangeChange("startDate", getDateFilterInputValue(date), "users");
                }}
                dateFormat="DD/MM/YYYY"
                timeFormat="HH:mm"
                inputProps={{
                  placeholder: "dd/MM/yyyy HH:mm",
                  className: "form-control",
                  style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                }}
                locale="pt-br"
              />
            </div>
            <InputGroup.Text onClick={() => setUserTableReset(previous => previous + 1)} style={{ cursor: 'pointer' }}>
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </InputGroup.Text>
          </InputGroup>
        </Form.Group>
        <Form.Group as={Col} xs={12} sm={6} md={4} className="mb-3">
          <InputGroup className="w-100 flex-nowrap">
            <div className="flex-fill">
              <Datetime
                value={
                  filter.dateRange.endDate
                    ? formatDateTimePickerValue(filter.dateRange.endDate)
                    : ""
                }
                onChange={(date) => {
                  handleDateRangeChange("endDate", getDateFilterInputValue(date), "users");
                }}
                dateFormat="DD/MM/YYYY"
                timeFormat="HH:mm"
                inputProps={{
                  placeholder: "dd/MM/yyyy HH:mm",
                  className: "form-control",
                  style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                }}
                locale="pt-br"
              />
            </div>
            <InputGroup.Text onClick={() => setUserTableReset(previous => previous + 1)} style={{ cursor: 'pointer' }}>
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </InputGroup.Text>
          </InputGroup>
        </Form.Group>
        <Col md={2} className="d-flex align-items-end mb-3">
          <Button
            variant="primary"
            onClick={() => {
              setFilter((prev) => ({
                ...prev,
                dateRange: getDefaultDateRangeMonth(),
              }));
              setUserTableReset(previous => previous + 1);
            }}
            title="Limpar filtros"
          >
            <FontAwesomeIcon icon={faEraser} />
          </Button>
        </Col>
      </Row>

      <div>
        {fieldsUsers.length === 0 ? (
          <div className="py-5">
            <Loader></Loader>
          </div>
        ) : (
          <ColumnSortingTable
            columns={fieldsUsers}
            title={dataTable.title + " - usuários gestores e expositores"}
            data={historyData.users}
            getCellProps={(cellinfo) => ({
              className:
                cellinfo.column.id === "icons" ? "table-icons-fourth" : "",
              style: {
                padding: cellinfo.column.id === "imagem" ? "0px 10px" : "",
                width: cellinfo.column.id === "imagem" ? "32px" : "",
              },
            })}
            triggerExport={triggerExportUsers}
            setTriggerExport={setTriggerExportUsers}
            onFilteredDataChange={setFilteredUsers}
            resetTrigger={JSON.stringify([resetTrigger, userTableReset])}
            pageResetTrigger={JSON.stringify(filter.dateRange)}
          />
        )}
      </div>
    </section>
  );
};

const CredentialCheckinDashboard02 = ({
  report,
  dataTable,
  filter,
  setFilter,
  handleDateRangeChange,
  getDefaultDateRangeMonth,
  setResetTrigger,
  handleDelete,
  canEdit,
  showHeader = true
}) => {
  if (!report) return null;

  const stats = report.stats || {};
  const chartData = report.chartData || {};
  const participantFieldOptions = [
    { value: '__all__', label: 'Nenhum' },
    ...(report.participantFilterOptions?.fields || []),
  ];
  const participantValueOptions = report.participantFilterOptions?.values || [];
  const selectedField = report.participantFilter?.field || null;
  const selectedValue = report.participantFilter?.value || null;
  const quickStats = [
    { key: 'registered', label: 'Inscritos', value: stats.registered || 0, icon: faUser },
    { key: 'checked-in', label: 'Presentes', value: stats.checkedIn || 0, icon: faCheckCircle },
    { key: 'missing', label: 'Ausentes', value: stats.missing || 0, icon: faTimesCircle },
    { key: 'rate', label: 'Taxa de check-in', value: `${stats.rate || 0}%`, icon: faCheckDouble },
  ];

  const pieOptions = {
    labels: chartData.pieChart?.labels || [],
    colors: [CHART_STATUS_COLORS.success, CHART_STATUS_COLORS.danger],
    legend: { position: 'bottom' },
  };

  const barOptions = {
    xaxis: { categories: chartData.hourlyChart?.labels || [] },
    colors: [CHART_STATUS_COLORS.success],
    plotOptions: { bar: { borderRadius: 3 } },
    legend: { position: 'top' },
  };

  return (
    <div className="credential-report">
      {showHeader && <Form.Group
        as={Col}
        className="textHeaderAplicativo d-flex justify-content-between align-items-center mb-3"
      >
        <h5 className="card-title mb-0">Check-ins ({report.allCheckinsTotal || 0})</h5>
        {canEdit && handleDelete && (
          <Dropdown className="d-inline">
            <Dropdown.Toggle className="btn-outline-primary">
              <FontAwesomeIcon icon={faCog} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => {
                  handleDelete(
                    {
                      platform: dataTable.platform,
                      evento_id: dataTable.evento_id,
                      type: "resetCheckins",
                    },
                    "Ao continuar, todo o histórico de registros de check-ins será excluído.",
                  );
                }}
              >
                Excluir todos registros de check-ins
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </Form.Group>}

      <Card className="bentoPanel bentoPanelGradient credential-report-panel mb-2">
        <Card.Body className="d-flex flex-column">
          <div className="bentoHeader">
            <div className="bentoIcon">
              <FontAwesomeIcon icon={faUsers} />
            </div>
            <h5 className="mb-0 fw-bold">Estatísticas</h5>
          </div>
          <div className="bentoStatsGrid bentoStatsGridSingleRow flex-grow-1 mt-3">
            {quickStats.map((stat, index) => (
              <div key={stat.key} className={`bentoStatCard ${index === 3 ? 'bg-success' : ''}`}>
                <div className={`bentoStatHeader ${index === 3 ? 'text-white' : ''}`}>
                  <FontAwesomeIcon icon={stat.icon} />
                </div>
                <div className="bentoStatBody">
                  <h2 className={index === 3 ? 'text-white' : ''}>{stat.value}</h2>
                  <span className={index === 3 ? 'text-white' : ''}>{stat.label}</span>
                </div>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>

      <Row className="align-items-end mx-0 gx-2 pt-3 mb-2">
        <Form.Group as={Col} sm={12} md={6} className="mb-2">
          <Form.Label className="me-2">Filtrar gráficos por campo</Form.Label>
          <div className="d-flex flex-row gap-2">
            <Select
              className="w-100"
              value={participantFieldOptions.find((option) => option.value === (selectedField || '__all__')) || participantFieldOptions[0]}
              onChange={(selected) => {
                const nextField = selected?.value === '__all__' ? null : selected?.value || null;
                setFilter((prevState) => ({
                  ...prevState,
                  participantFieldFilter: nextField,
                  participantValueFilter: null,
                }));
              }}
              options={participantFieldOptions}
              placeholder="Selecione um campo"
              styles={credentialSelectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {selectedField && (
              <Select
                className="w-100"
                value={participantValueOptions.find((option) => option.value === selectedValue) || null}
                onChange={(selected) => {
                  setFilter((prevState) => ({
                    ...prevState,
                    participantValueFilter: selected?.value || null,
                  }));
                }}
                options={participantValueOptions}
                placeholder="Selecione um valor"
                isClearable
                styles={credentialSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                noOptionsMessage={() => 'Nenhum valor encontrado'}
              />
            )}
          </div>
        </Form.Group>
        <Form.Group as={Col} sm={12} md={6} className="mb-2">
          <Row className="gx-2">
            <Col xs={12} sm={5}>
              <Form.Label>Data de início</Form.Label>
              <InputGroup className="w-100 flex-nowrap">
                <div className="flex-fill">
                  <Datetime
                    className="w-100"
                    value={formatDateTimePickerValue(filter?.dateRange?.startDate)}
                    onChange={(date) => {
                      handleDateRangeChange('startDate', getDateFilterInputValue(date), 'credentialCharts');
                    }}
                    dateFormat="DD/MM/YYYY"
                    timeFormat="HH:mm"
                    inputProps={{ placeholder: 'dd/MM/yyyy HH:mm', className: 'form-control', style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 } }}
                    locale="pt-br"
                  />
                </div>
                <InputGroup.Text onClick={() => setResetTrigger && setResetTrigger(prev => prev + 1)} style={{ cursor: 'pointer' }}>
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </InputGroup.Text>
              </InputGroup>
            </Col>
            <Col xs={12} sm={5} className="mt-3 mt-sm-0">
              <Form.Label>Data de término</Form.Label>
              <InputGroup className="w-100 flex-nowrap">
                <div className="flex-fill">
                  <Datetime
                    className="w-100"
                    value={formatDateTimePickerValue(filter?.dateRange?.endDate)}
                    onChange={(date) => {
                      handleDateRangeChange('endDate', getDateFilterInputValue(date), 'credentialCharts');
                    }}
                    dateFormat="DD/MM/YYYY"
                    timeFormat="HH:mm"
                    inputProps={{ placeholder: 'dd/MM/yyyy HH:mm', className: 'form-control', style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 } }}
                    locale="pt-br"
                  />
                </div>
                <InputGroup.Text onClick={() => setResetTrigger && setResetTrigger(prev => prev + 1)} style={{ cursor: 'pointer' }}>
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </InputGroup.Text>
              </InputGroup>
            </Col>
            <Col xs={12} sm={2} className="d-flex align-items-end mt-3 mt-sm-0">
              <Button
                variant="primary"
                onClick={() => {
                  setFilter((prevState) => ({
                    ...prevState,
                    dateRange: getDefaultDateRangeMonth(),
                    participantFieldFilter: null,
                    participantValueFilter: null,
                  }));
                  setResetTrigger && setResetTrigger(prev => prev + 1);
                }}
                title="Limpar filtros dos gráficos"
              >
                <FontAwesomeIcon icon={faEraser} />
              </Button>
            </Col>
          </Row>
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Col lg={6} md={12} sm={12} className="mb-2">
          <Card className="w-100">
            <Card.Body>
              <h6 className="mb-3">{chartData.pieChartTitle || 'Total de participantes'} ({chartData.pieChartParticipants || 0})</h6>
              <Chart type="donut" height={300} options={pieOptions} series={chartData.pieChart?.datasets?.[0]?.data || []} />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6} md={12} sm={12} className="mb-2">
          <Card className="w-100">
            <Card.Body>
              <h6 className="mb-3">{chartData.chartTitle || 'Total de check-ins'} ({chartData.checkinsInRange || 0})</h6>
              <Chart type="bar" height={300} options={barOptions} series={(chartData.hourlyChart?.datasets || []).map((dataset) => ({ name: dataset.label, data: dataset.data }))} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export const CheckinsSection02 = ({
  historyData,
  dataTable,
  filterCredentialCharts,
  setFilterCredentialCharts,
  handleDateRangeChange,
  getDefaultDateRangeMonth,
  setResetTrigger,
  handleDelete,
  canEdit,
  showHeader = true
}) => {
  const credentialReport = historyData.checkinReport;

  if (!credentialReport) return null;

  return (
    <CredentialCheckinDashboard02
      report={credentialReport}
      showHeader={showHeader}
      dataTable={dataTable}
      filter={filterCredentialCharts}
      setFilter={setFilterCredentialCharts}
      handleDateRangeChange={handleDateRangeChange}
      getDefaultDateRangeMonth={getDefaultDateRangeMonth}
      setResetTrigger={setResetTrigger}
      handleDelete={handleDelete}
      canEdit={canEdit}
    />
  );
};

const getRankingParticipantName = (participant, dataTable) => {
  const fields = dataTable?.json?.form?.fields || [];
  const nameField = fields.find((field) => /^(nome|name)$/i.test(String(field?.label || field?.inputID || "").trim()))
    || fields.find((field) => /nome|name/i.test(String(field?.label || field?.inputID || "")));
  return (nameField && participant?.[nameField.inputID]) || participant?.name || participant?.nome || participant?.email || "Participante";
};

export const prepareRankingRows = (participants, dataTable) => (
  (Array.isArray(participants) ? participants : []).map((participant) => ({
    ...participant,
    ranking_name: getRankingParticipantName(participant, dataTable),
    rank_position: Number(participant.rank_position || 0),
    total_value: Number(participant.total_value || 0),
    achievements_completed: Number(participant.achievements_completed || 0),
    achievements_total: Number(participant.achievements_total || 0),
  })).sort((firstParticipant, secondParticipant) => (
    // rank_position is authoritative server data; total_value is display data,
    // not a client-side fallback for calculating the ranking.
    (firstParticipant.rank_position || Number.MAX_SAFE_INTEGER) - (secondParticipant.rank_position || Number.MAX_SAFE_INTEGER)
    || Number(firstParticipant.user_id || 0) - Number(secondParticipant.user_id || 0)
  ))
);

export const buildRankingExportConfig = (participants, dataTable) => {
  const rows = prepareRankingRows(participants, dataTable);
  return {
    filename: `${dataTable.title || dataTable.titulo || "Evento"} - ranking`,
    sheet: {
      data: [
        ["Posição", "Participante", "E-mail", "Pontuação", "Conquistas"].map((value) => ({ value, type: "string" })),
        ...rows.map((participant) => [
          participant.rank_position
            ? { value: participant.rank_position, type: "number" }
            : { value: "", type: "string" },
          { value: participant.ranking_name || "", type: "string" },
          { value: participant.email || "", type: "string" },
          { value: participant.total_value, type: "number" },
          { value: `${participant.achievements_completed}/${participant.achievements_total}`, type: "string" },
        ]),
      ],
    },
  };
};

export const RankingParticipantsList = ({ participants, dataTable, canEdit, onScoreHistoryChange, listRef }) => {
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const rows = useMemo(() => prepareRankingRows(participants, dataTable), [participants, dataTable]);
  if (!rows.length) return <div className="text-center text-muted py-5">Nenhum participante ativo no ranking.</div>;
  return (
    <>
      <ParticipantScoreHistoryModal
        dataTable={dataTable}
        canEdit={canEdit}
        onChanged={onScoreHistoryChange}
        onHide={() => setSelectedParticipant(null)}
        participant={selectedParticipant}
        show={Boolean(selectedParticipant)}
      />
      <div ref={listRef} className="list-group participant-ranking-list mb-0">
        {rows.map((participant) => (
          <div className="list-group-item participant-ranking-item" key={participant.user_id}>
            <span className="participant-ranking-position">#{participant.rank_position || "—"}</span>
            {participant.image ? <img className="participant-ranking-avatar" src={participant.image} alt="" /> : <span className="participant-ranking-avatar participant-ranking-avatar--fallback"><FontAwesomeIcon icon={faUser} /></span>}
            <span className="participant-ranking-identity"><strong>{participant.ranking_name}</strong>{participant.email && <small>{participant.email}</small>}</span>
            <span className="participant-ranking-metric"><small>Pontuação</small><strong>{participant.total_value}</strong></span>
            <span className="participant-ranking-metric"><small>Conquistas</small><strong>{participant.achievements_completed}/{participant.achievements_total}</strong></span>
            <OverlayTrigger placement="top" overlay={<Tooltip>Ver pontuação</Tooltip>}>
              <span className="d-inline-block">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  aria-label="Ver pontuação"
                  onClick={() => setSelectedParticipant(participant)}
                >
                  <FontAwesomeIcon icon={faMedal} />
                </button>
              </span>
            </OverlayTrigger>
          </div>
        ))}
      </div>
    </>
  );
};

export const EventRecordsSummarySection02 = ({ historyData, dataTable, refreshKey, onViewParticipants, onViewRsvpTable }) => {
  const checkinsAvailable = historyData.checkinsAvailable === true;
  const [registryState, setRegistryState] = useState({ registry: null, loading: true, error: "" });
  const [registryReload, setRegistryReload] = useState(0);
  const registry = registryState.registry;
  const attendanceDefinition = useMemo(() => registry?.definitions?.find((item) => item.definition_id === registry.attendance_definition_id) || null, [registry]);
  const rsvpAvailable = !registryState.loading && !registryState.error;
  const allRecordRows = useMemo(() => participantRecordRows(historyData.allParticipants || [], attendanceDefinition, true, checkinsAvailable, rsvpAvailable), [checkinsAvailable, attendanceDefinition, historyData.allParticipants, rsvpAvailable]);

  useEffect(() => {
    const controller = new AbortController();
    setRegistryState({ registry: null, loading: true, error: "" });
    getEventRsvpResponses({ eventId: dataTable.evento_id, pageSize: 1, signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) return;
        if (!Array.isArray(response.data?.registry?.definitions)) throw new Error("RSVP registry unavailable");
        setRegistryState({ registry: response.data.registry, loading: false, error: "" });
      })
      .catch((failure) => {
        if (controller.signal.aborted) return;
        const availableRegistry = failure.response?.data?.code === "RSVP_ATTENDANCE_REQUIRED" ? failure.response.data.registry : null;
        setRegistryState(Array.isArray(availableRegistry?.definitions) ? { registry: availableRegistry, loading: false, error: "" } : { registry: null, loading: false, error: "Não foi possível carregar os dados de RSVP." });
      });
    return () => controller.abort();
  }, [dataTable.evento_id, refreshKey, registryReload]);


  return <>
    <ParticipantRecordsSummary records={allRecordRows} dataTable={dataTable} definition={attendanceDefinition} loading={registryState.loading} error={registryState.error} checkinsAvailable={checkinsAvailable} onRetry={() => setRegistryReload((value) => value + 1)} onViewParticipants={onViewParticipants} onViewRsvpTable={onViewRsvpTable} />
  </>;
};

export const ParticipantsSection02 = ({
  mode = "records", historyData, triggerExport, setTriggerExport, zipIsReady, setZipIsReady,
  filteredParticipants, dataTable, notyf, filter, setFilter, handleDelete, handleNewUser,
  handleDateRangeChange, getDefaultDateRangeMonth, getDefaultCheckinDateRange, fields, setFilteredParticipants, canEdit,
  resetTrigger, downloadFilteredSignatures, isDownloadingSignatureArchive, signatureEnabled, children, tableRef
}) => {
  const isCheckinsMode = mode === "checkins";
  const credentialReport = historyData.checkinReport;
  const filterOptionGroups = credentialReport?.filterOptionGroups || [];
  const checkinsAvailable = historyData.checkinsAvailable === true;
  const reportUnavailable = isCheckinsMode && !checkinsAvailable;
  const selectedCheckinFilter = Number(filter.checkin_filter ?? 2);
  const selectedCheckinOption = credentialReport?.filterOptions?.find((option) => option.value === selectedCheckinFilter);
  const showCheckinDateRange = isCheckinsMode && selectedCheckinFilter !== 0 && selectedCheckinFilter !== 2;
  const [recordTableReset, setRecordTableReset] = useState(0);
  const [pendingCheckinDateRange, setPendingCheckinDateRange] = useState(filter.checkinDateRange || {});
  const tableRecordRows = useMemo(() => isCheckinsMode ? credentialReport?.participants || [] : (historyData.participants || []).map((participant) => {
    const status = participantRegistrationStatus(participant);
    return { ...participant, record_registration_choice: status, record_registration_status: RECORD_REGISTRATION_LABELS[status] };
  }).filter((participant) => !filter.registrationStatus || participant.record_registration_choice === filter.registrationStatus), [credentialReport?.participants, filter.registrationStatus, historyData.participants, isCheckinsMode]);
  const recordColumns = useMemo(() => {
    const baseColumns = fields.filter((column) => isCheckinsMode
      ? !["creation_date_formatted", "register_date_formatted"].includes(column.accessor)
      : !["data_checkin_formatted", "checkin_origin", "checkin_ip", "checkin_location", "attendance_type", "signature_status", "status_formatted"].includes(column.accessor))
      .map((column) => column.accessor === "register_date_formatted" ? { ...column, Header: "Conclusão do cadastro" } : column.accessor === "status_formatted" ? { ...column, Header: "Status" } : column);
    const statusColumns = isCheckinsMode
      ? baseColumns.filter((column) => column.accessor === "status_formatted")
      : [{ Header: "Status", accessor: "record_registration_status" }];
    const participantColumns = baseColumns.filter((column) => column.accessor !== "status_formatted");
    const formFieldIds = new Set((dataTable.json?.form?.fields || [])
      .filter((field) => field?.inputID !== undefined && field?.inputID !== null)
      .map((field) => String(field.inputID)));
    const emailColumns = participantColumns.filter((column) => column.accessor === "email");
    const formColumns = participantColumns.filter((column) => typeof column.accessor === "string" && formFieldIds.has(column.accessor));
    const userMetadataAccessors = new Set([
      "user_id", "external_user_id", "origin", "rank_position", "total_value", "total_exhibitors_collected",
      "access_code", "last_access_formatted", "access_code_used", "access_code_expires_formatted", "push_subscription",
    ]);
    const additionalUserColumns = filter.extraPropsParticipants
      ? participantColumns.filter((column) => userMetadataAccessors.has(column.accessor))
      : [];
    const actionColumns = participantColumns.filter((column) => column.accessor === "icons");
    const specificColumns = participantColumns.filter((column) =>
      column.accessor !== "email"
      && column.accessor !== "icons"
      && !formFieldIds.has(String(column.accessor))
      && !userMetadataAccessors.has(column.accessor),
    );
    return [
      ...emailColumns,
      ...formColumns,
      ...statusColumns,
      ...additionalUserColumns,
      ...specificColumns,
      ...actionColumns,
    ];
  }, [dataTable.json, fields, filter.extraPropsParticipants, isCheckinsMode]);

  const tableTotal = filteredParticipants?.length ?? tableRecordRows.length;
  useEffect(() => {
    setPendingCheckinDateRange(filter.checkinDateRange || {});
  }, [filter.checkinDateRange]);

  const handleParticipantExport = () => setTriggerExport(true);
  const applyCheckinDateRange = () => {
    const normalizedRange = normalizeDateRange(pendingCheckinDateRange);
    if (!normalizedRange.valid) {
      notyf.open({ type: "danger", message: "Informe um intervalo de datas válido.", ripple: true, dismissible: true });
      return;
    }
    setFilter((previous) => ({ ...previous, checkinDateRange: { startDate: normalizedRange.startDate, endDate: normalizedRange.endDate } }));
  };

  return <section className={isCheckinsMode ? "mb-4" : "p-2 mb-4"} aria-label={isCheckinsMode ? "Check-ins" : "Lista de participantes"}>
    <Form.Group as={Col} className="textHeaderAplicativo d-flex flex-wrap gap-2 justify-content-between align-items-center">
      <h5 className="card-title mb-0">{isCheckinsMode ? `Check-ins (${tableTotal})` : `Lista de participantes (${tableTotal})`}</h5>
      <div>
          <Dropdown className="d-inline">
            <Dropdown.Toggle className="btn btn-outline-primary" aria-label={isCheckinsMode ? "Opções de check-ins" : "Opções do relatório de participantes"}>
              <FontAwesomeIcon icon={faCog} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                disabled={reportUnavailable || fields.length === 0}
                onClick={handleParticipantExport}
              >
                Exportar
              </Dropdown.Item>
              <Dropdown.Item
                disabled={!zipIsReady || reportUnavailable}
                onClick={() => {
                  if (zipIsReady) {
                    batchQRCodeDownload(
                      setZipIsReady,
                      filteredParticipants,
                      dataTable.titulo,
                      dataTable.token,
                      notyf,
                      dataTable.platform,
                    );
                  }
                }}
              >
                {zipIsReady ? "Exportar Códigos QR's" : "Gerando QR's..."}
              </Dropdown.Item>
              {isCheckinsMode && signatureEnabled && canEdit && (
                <Dropdown.Item
                  disabled={isDownloadingSignatureArchive || reportUnavailable}
                  onClick={() => downloadFilteredSignatures(filteredParticipants)}
                >
                  {isDownloadingSignatureArchive ? "Gerando assinaturas..." : "Exportar todas as assinaturas"}
                </Dropdown.Item>
              )}
              <Dropdown.Item
                  onClick={() => {
                    setFilter((prevState) => ({
                      ...prevState,
                      extraPropsParticipants:
                        !prevState.extraPropsParticipants,
                    }));
                  }}
                >
                  {!filter.extraPropsParticipants
                    ? "Mostrar informações adicionais"
                    : "Esconder informações adicionais"}
                </Dropdown.Item>
              {canEdit && !isCheckinsMode && (
                <Dropdown.Item
                  onClick={() => {
                    const props = buildPlatform2UserAction({
                      dataTable,
                      action: PLATFORM_2_USER_ACTIONS.DELETE,
                      userType: 0,
                    });
                    handleDelete(
                      props,
                      "Ao continuar, todos os participantes registrados serão excluídos.",
                    );
                  }}
                >
                  Excluir participantes
                </Dropdown.Item>
              )}
              {canEdit && !isCheckinsMode && (
                <Dropdown.Item
                  onClick={() => {
                    handleDelete(
                      buildPlatform2UserAction({
                        dataTable,
                        action: PLATFORM_2_USER_ACTIONS.RESET_SCORE_HISTORY,
                        userType: 0,
                        scope: "all",
                      }),
                      "Ao continuar, a pontuação e os dados usados no ranking dos participantes serão resetados. As atividades e conquistas serão mantidas.",
                    );
                  }}
                >
                  Excluir apenas histórico de pontuação
                </Dropdown.Item>
              )}
              {canEdit && !isCheckinsMode && (
                <Dropdown.Item
                  onClick={() => {
                    const props = buildPlatform2UserAction({
                      dataTable,
                      action: PLATFORM_2_USER_ACTIONS.RESET_HISTORY,
                      userType: 0,
                    });
                    handleDelete(
                      props,
                      "Mantém os registros, mas remove todo o histórico de uso dos participantes, incluindo analytics, transmissões, chat, Newsfeed, pontuação e atividades.",
                    );
                  }}
                >
                  Excluir histórico do participante
                </Dropdown.Item>
              )}
              {canEdit && isCheckinsMode && (
                <Dropdown.Item
                  disabled={reportUnavailable}
                  onClick={() => {
                    handleDelete(
                      {
                        platform: dataTable.platform,
                        evento_id: dataTable.evento_id,
                        type: "resetCheckins",
                      },
                      "Ao continuar, todo o histórico de registros de check-ins será excluído.",
                    );
                  }}
                >
                  Excluir todos registros de check-ins
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
          {canEdit && !isCheckinsMode && (
            <Button
              type="button"
              className="btn btn-outline-primary ms-2"
              aria-label="Adicionar participante"
              onClick={() => {
                handleNewUser(0);
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
            </Button>
          )}
        </div>
    </Form.Group>
    <h6 className="text-muted mt-3 mx-2">{isCheckinsMode ? "Acompanhamento do processo de check-ins dos participantes" : "Encontre informações de registro e resultados associados aos participantes"}</h6>
    {children}
    {(!isCheckinsMode || credentialReport) && <hr className="my-2" />}
    {isCheckinsMode && !checkinsAvailable ? <div className="alert alert-warning">Os dados de check-in não foram carregados. Isso não significa que não existam registros.</div> : <>
      {!isCheckinsMode && <>
        <div className="pt-3">
          <ReportDateRangeFilter idPrefix="participants-creation" dateRange={filter.dateRange} startLabel="Data de criação inicial" endLabel="Data de criação final"
            onChange={(key, value) => handleDateRangeChange(key, value, "participants")}
            onApply={() => setRecordTableReset((value) => value + 1)}
            onReset={() => setFilter((previous) => ({ ...previous, dateRange: getDefaultDateRangeMonth() }))}
          >
            <Form.Group as={Col} xs={12} sm={6} md={3} className="mb-3" controlId="records-registration-status">
              <Form.Label className="fw-bold">Status</Form.Label>
              <Form.Select value={filter.registrationStatus || ""} onChange={(event) => setFilter((previous) => ({ ...previous, registrationStatus: event.target.value }))}>
                <option value="">Todos</option>{Object.entries(RECORD_REGISTRATION_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </Form.Select>
            </Form.Group>
          </ReportDateRangeFilter>
        </div>
      </>}
      {isCheckinsMode && (
      <Row className="pt-3 mb-3 mx-0 gx-2 align-items-end">
          <Form.Group as={Col} xs={12} md={4} className="mb-3">
            <Form.Label>Filtro de check-in</Form.Label>
            <Select
              value={selectedCheckinOption}
              onChange={(selected) => {
                setFilter((prev) => ({
                  ...prev,
                  checkin_filter: selected?.value ?? 2,
                }));
              }}
              options={filterOptionGroups}
              isSearchable={false}
              styles={credentialSelectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </Form.Group>
          {showCheckinDateRange && (
            <>
          <Form.Group as={Col} xs={12} sm={6} md={3} className="mb-3">
            <Form.Label>Data de início</Form.Label>
            <InputGroup className="w-100 flex-nowrap">
              <div className="flex-fill">
                <Datetime
                  value={
                    pendingCheckinDateRange.startDate
                      ? formatDateTimePickerValue(pendingCheckinDateRange.startDate)
                      : ""
                  }
                  onChange={(date) => {
                    setPendingCheckinDateRange((prev) => ({
                      ...prev,
                      startDate: getDateFilterInputValue(date),
                    }));
                  }}
                  dateFormat="DD/MM/YYYY"
                  timeFormat="HH:mm"
                  inputProps={{
                    placeholder: "dd/MM/yyyy HH:mm",
                    className: "form-control",
                    style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                  }}
                  locale="pt-br"
                />
              </div>
              <InputGroup.Text onClick={applyCheckinDateRange} style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </InputGroup.Text>
            </InputGroup>
          </Form.Group>
          <Form.Group as={Col} xs={12} sm={6} md={3} className="mb-3">
            <Form.Label>Data de término</Form.Label>
            <InputGroup className="w-100 flex-nowrap">
              <div className="flex-fill">
                <Datetime
                  value={
                    pendingCheckinDateRange.endDate
                      ? formatDateTimePickerValue(pendingCheckinDateRange.endDate)
                      : ""
                  }
                  onChange={(date) => {
                    setPendingCheckinDateRange((prev) => ({
                      ...prev,
                      endDate: getDateFilterInputValue(date),
                    }));
                  }}
                  dateFormat="DD/MM/YYYY"
                  timeFormat="HH:mm"
                  inputProps={{
                    placeholder: "dd/MM/yyyy HH:mm",
                    className: "form-control",
                    style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                  }}
                  locale="pt-br"
                />
              </div>
              <InputGroup.Text onClick={applyCheckinDateRange} style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </InputGroup.Text>
            </InputGroup>
          </Form.Group>
            </>
          )}
          <Col md={2} className="d-flex align-items-end mb-3">
            <Button
              variant="primary"
              onClick={() => {
                const defaults = getDefaultCheckinDateRange();
                setPendingCheckinDateRange(defaults);
                setFilter((prev) => ({
                  ...prev,
                  checkin_filter: 2,
                  checkinDateRange: defaults,
                }));
                setRecordTableReset((value) => value + 1);
              }}
              title="Limpar filtros de check-in"
            >
              <FontAwesomeIcon icon={faEraser} />
            </Button>
          </Col>
        </Row>
      )}
      <div ref={tableRef}>
        {fields.length === 0 ? <div className="py-5"><Loader /></div> : <ColumnSortingTable
          key={`${mode}:${fields.length}:${filter.checkin_filter || ""}:${filter.registrationWindowMinutes || ""}`}
          columns={recordColumns} data={tableRecordRows} title={`${dataTable.title} - ${isCheckinsMode ? "check-ins" : "registros de participantes"}`}
          exportMetadata={[
            ...(!isCheckinsMode ? [
              { label: "Status", value: RECORD_REGISTRATION_LABELS[filter.registrationStatus] || "Todos" },
              { label: "Criação inicial", value: filter.dateRange?.startDate || "Sem limite" },
              { label: "Criação final", value: filter.dateRange?.endDate || "Sem limite" },
            ] : [
              { label: "Filtro de check-in", value: selectedCheckinOption?.label || "Todos" },
              { label: "Check-in inicial", value: showCheckinDateRange ? filter.checkinDateRange?.startDate || "Sem limite" : "Não se aplica" },
              { label: "Check-in final", value: showCheckinDateRange ? filter.checkinDateRange?.endDate || "Sem limite" : "Não se aplica" },
            ]),
            { label: "Fuso dos cadastros e check-ins", value: PANEL_EVENT_TIME_ZONE },
          ]}
          getCellProps={(cellinfo) => ({ className: cellinfo.column.id === "icons" ? "table-icons-fourth" : "", style: { padding: cellinfo.column.id === "imagem" ? "0px 10px" : "", width: cellinfo.column.id === "imagem" ? "32px" : "" } })}
          triggerExport={triggerExport} setTriggerExport={setTriggerExport} onFilteredDataChange={setFilteredParticipants}
          resetTrigger={JSON.stringify([resetTrigger, recordTableReset])}
          pageResetTrigger={JSON.stringify([filter.registrationStatus, filter.dateRange, filter.checkinDateRange])}
        />}
      </div>
    </>}
  </section>;
};

export const RsvpRecordsSection02 = ({ historyData, dataTable, fields, refreshKey, onResponsesChanged, canManageRsvps = false, sectionRef, tableRef }) => {
  const [state, setState] = useState({ total: 0, loading: true, canExport: false, resetCount: 0 });
  const [showMoreInformation, setShowMoreInformation] = useState(false);
  const [showRsvpManager, setShowRsvpManager] = useState(false);
  const reportRef = useRef(null);
  return <section className="mb-4" aria-label="Respostas RSVP" ref={sectionRef}>
    <div className="textHeaderAplicativo d-flex justify-content-between align-items-center gap-2 flex-wrap">
      <h5 className="card-title mb-0">RSVPs ({state.loading ? "…" : state.total})</h5>
      <div className="d-flex align-items-center gap-2">
        {canManageRsvps && <Button variant="outline-primary" onClick={() => setShowRsvpManager(true)}><FontAwesomeIcon icon={faCog} className="me-2" />Gerenciar RSVPs</Button>}
        <Dropdown>
          <Dropdown.Toggle className="btn-outline-primary" aria-label="Opções dos participantes RSVP"><FontAwesomeIcon icon={faCog} /></Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item disabled={!state.canExport} onClick={() => reportRef.current?.exportResponses()}>{state.exporting ? "Exportando…" : "Exportar"}</Dropdown.Item>
            <Dropdown.Item disabled={!state.canResetAll || state.exporting || state.resetting} onClick={() => reportRef.current?.openBulkResetConfirmation()}>Excluir histórico de resposta</Dropdown.Item>
            <Dropdown.Item disabled={state.exporting || state.resetting} onClick={() => setShowMoreInformation((value) => !value)}>{showMoreInformation ? "Esconder informações adicionais" : "Mostrar informações adicionais"}</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
    <h6 className="text-muted mt-3 mx-2">Selecione um RSVP para consultar as respostas e o histórico dos participantes. Apenas o RSVP principal representa a confirmação de presença</h6>
    <EventRsvpResponsesCard ref={reportRef} eventId={dataTable.evento_id} eventTimezone={dataTable.timezone || "America/Sao_Paulo"} refreshKey={refreshKey}
      onStateChange={setState} participantColumns={fields} participantFormFields={dataTable.json?.form?.fields || []}
      participantEmailLabel={dataTable.json?.form?.emailLabel || "E-mail"}
      participantRecords={historyData.allParticipants} showMoreInformation={showMoreInformation}
      showSummary={false} showDistribution onResponsesChanged={onResponsesChanged} tableContainerRef={tableRef} />
    {canManageRsvps && <EventRsvpManagerModal
      show={showRsvpManager}
      onHide={() => setShowRsvpManager(false)}
      eventId={dataTable.evento_id}
      eventTimezone={dataTable.timezone || "America/Sao_Paulo"}
      registrationEndDate={dataTable.json?.form?.register_end_date}
      refreshKey={refreshKey}
      onSaved={() => onResponsesChanged?.()}
    />}
  </section>;
};
